import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const NavButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 100;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  display: none;
  touch-action: manipulation;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const NavIcon = styled.div<{ isOpen: boolean }>`
  width: 30px;
  height: 20px;
  position: relative;
  transform: ${props => props.isOpen ? 'rotate(45deg)' : 'none'};
  transition: transform 0.3s ease;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--accent-color);
    border-radius: 2px;
    transition: all 0.3s ease;

    &:first-child {
      top: ${props => props.isOpen ? '50%' : '0'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'none'};
    }

    &:nth-child(2) {
      top: 50%;
      transform: translateY(-50%);
      opacity: ${props => props.isOpen ? '0' : '1'};
    }

    &:last-child {
      bottom: ${props => props.isOpen ? '50%' : '0'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'none'};
    }
  }
`;

const NavMenu = styled(motion.nav)`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  background: var(--bg-primary);
  opacity: 0.98;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 99;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
`;

const NavLink = styled(motion.a)`
  color: var(--text-primary);
  text-decoration: none;
  font-size: 2rem;
  margin: 1rem 0;
  position: relative;
  cursor: pointer;
  padding: 0.5rem 1rem;
  width: 100%;
  text-align: center;
  touch-action: manipulation;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0.5rem 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: var(--accent-color);
    transition: width 0.3s ease;
  }

  &:hover::after,
  &:active::after {
    width: 80%;
  }
`;

interface MobileNavProps {
  onSectionChange: (section: number) => void;
}

const MobileNav: React.FC<MobileNavProps> = ({ onSectionChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      if (isOpen && e.target instanceof HTMLElement && !e.target.closest('nav')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    return () => document.removeEventListener('touchstart', handleTouchStart);
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleSectionClick = (section: number) => {
    onSectionChange(section);
    setIsOpen(false);
  };

  return (
    <>
      <NavButton onClick={toggleMenu} aria-label="Toggle navigation menu">
        <NavIcon isOpen={isOpen}>
          <span />
          <span />
          <span />
        </NavIcon>
      </NavButton>

      <AnimatePresence>
        {isOpen && (
          <NavMenu
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <NavLink
              onClick={() => handleSectionClick(0)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Home
            </NavLink>
            <NavLink
              onClick={() => handleSectionClick(1)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Journey
            </NavLink>
            <NavLink
              onClick={() => handleSectionClick(2)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Projects
            </NavLink>
            <NavLink
              onClick={() => handleSectionClick(3)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Skills
            </NavLink>
            <NavLink
              onClick={() => handleSectionClick(4)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Certificates
            </NavLink>
            <NavLink
              onClick={() => handleSectionClick(5)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact
            </NavLink>
          </NavMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileNav; 